<template>
  <div class="living-person">
    <Checkbox
      :checked="selectedRef"
      :disabled="disabledRef"
      @update:checked="selectedRef = !selectedRef"/>
    <div>
      {{ props.person?.displayName }}
      <span v-if="subtextItemsRef.length" class="subtext">
        ({{ subtextItemsRef.join(', ') }})
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.living-person {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-size: 0.875rem;

  .subtext {
    font-size: 0.75rem;
    color: #aaa;
  }
}
</style>

<script lang="ts" setup>
import { computed } from 'vue'
import { Person } from '@webapp/rd/ResearchDataModel'
import { useSyncSessionStore } from './SyncSessionStore'
import Checkbox from '@webapp/util/Checkbox.vue'

const props = defineProps<{
  person: Person
  sessionId: string
}>()

const syncSessionStore = useSyncSessionStore()
const stateRef = computed(() => syncSessionStore.getState(props.sessionId))

const subtextItemsRef = computed(() => {
  const items: (string | number)[] = []
  if (props.person.displayProperties.birthYear) {
    items.push(props.person.displayProperties.birthYear)
  }
  if (props.person.displayProperties.assumeDeceased) {
    items.push('deceased')
  }
  return items
})

const isMappedRef = computed(() =>
  stateRef.value?.sourceGraph?.mappedPersonIds.has(props.person.id!) ?? false)
const isInLivingWindowRef = computed(() =>
  stateRef.value?.sourceGraph?.livingWindowPersonIds.has(props.person.id!) ?? false)
const isIncludedRef = computed(() =>
  stateRef.value?.livingWindowPersonIds.includes(props.person.id!) ?? false)

const selectedRef = computed({
  get: () => isMappedRef.value || isIncludedRef.value || !isInLivingWindowRef.value,
  set: value => {
    if (!isInLivingWindowRef.value) {
      throw new Error('Person not in living window')
    }
    const includeIds = stateRef.value?.livingWindowPersonIds
    if (!includeIds) {
      return
    }
    if (value) {
      if (!includeIds.includes(props.person.id!)) {
        includeIds.push(props.person.id!)
      }
    }
    else {
      const index = includeIds.indexOf(props.person.id!)
      if (index > -1) {
        includeIds.splice(index, 1)
      }
    }
  },
})

const disabledRef = computed(() => isMappedRef.value || !isInLivingWindowRef.value)
</script>
